import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';
import { NgapAct } from '../types/ngap';
import { SimulatorCartItem } from '../features/simulator/types';
import { deslugifyHashtag, generateSlug } from '../utils/urlUtils';
import { updatePageMeta, generateHashtagPageMeta, updateJsonLd, generateHashtagSchema } from '../utils/seoUtils';
import { trackPageView } from '../utils/googleAnalytics';
import { Hash, ArrowLeft } from 'lucide-react';

// Components
import ResultsGrid from './ResultsGrid';
import Breadcrumb from './Breadcrumb';
import SkeletonGrid from './SkeletonGrid';
import AppinesBanner from './AppinesBanner';
import Footer from './Footer';

interface HashtagPageProps {
  darkMode: boolean;
  cartItems?: SimulatorCartItem[];
  onToggleCartItem?: (act: NgapAct) => void;
  displayCotationType?: 'current' | 'july2025' | 'july2026' | 'sept2026' | 'july2027';
  onHashtagClick?: (hashtag: string) => void;
}

const HashtagPage: React.FC<HashtagPageProps> = ({
  darkMode,
  cartItems = [],
  onToggleCartItem,
  displayCotationType,
  onHashtagClick
}) => {
  const { hashtag_slug } = useParams<{ hashtag_slug: string }>();
  const navigate = useNavigate();
  
  const [acts, setActs] = useState<NgapAct[]>([]);
  const [filteredActs, setFilteredActs] = useState<NgapAct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasTrackedPageView, setHasTrackedPageView] = useState(false);

  // Récupérer le hashtag original à partir du slug
  const originalHashtag = hashtag_slug ? deslugifyHashtag(hashtag_slug) : '';

  // Charger les données depuis Supabase
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: supabaseError } = await supabase
          .from('ngap_cotations')
          .select('*')
          .order('lettre_cle', { ascending: true })
          .order('cotation_valeur', { ascending: true });

        if (supabaseError) {
          throw supabaseError;
        }

        if (data) {
          setActs(data);
        }
      } catch (err) {
        console.error('Error loading data:', err);
        const errorMessage = err instanceof Error ? err.message : 'Unknown error';
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  // Filtrer les actes par hashtag
  const filteredActsByHashtag = useMemo(() => {
    if (!originalHashtag || !acts.length) return [];

    return acts.filter(act => {
      if (!act.pathologies || !act.pathologies.trim()) return false;
      
      // Diviser les pathologies par virgule et vérifier si le hashtag est présent
      const pathologies = act.pathologies.split(',').map(p => p.trim());
      return pathologies.some(pathology => 
        generateSlug(pathology) === hashtag_slug
      );
    });
  }, [acts, hashtag_slug]);

  // Mettre à jour les actes filtrés
  useEffect(() => {
    setFilteredActs(filteredActsByHashtag);
  }, [filteredActsByHashtag]);

  // Mettre à jour les métadonnées SEO
  useEffect(() => {
    if (originalHashtag && filteredActsByHashtag.length >= 0) {
      const meta = generateHashtagPageMeta(originalHashtag, filteredActsByHashtag.length);
      updatePageMeta(undefined, meta);

      // Update JSON-LD structured data
      const jsonLdSchema = generateHashtagSchema(originalHashtag, filteredActsByHashtag);
      updateJsonLd(jsonLdSchema, 'hashtag-page-jsonld');

      // Track page view only once
      if (!hasTrackedPageView) {
        trackPageView(`Hashtag ${originalHashtag}`, `/cotations/hashtag/${hashtag_slug}`);
        setHasTrackedPageView(true);
      }
    }
  }, [originalHashtag, filteredActsByHashtag.length, hashtag_slug, hasTrackedPageView, filteredActsByHashtag]);

  // Générer le breadcrumb
  const breadcrumbItems = [
    { label: 'Accueil', url: '/' },
    { label: 'Cotations', url: '/' },
    { label: `#${originalHashtag}`, url: `/cotations/hashtag/${hashtag_slug}` }
  ];

  // Gérer les erreurs de chargement
  if (error) {
    return (
      <div className="min-h-screen transition-colors duration-300" style={{
        background: darkMode 
          ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
          : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
      }}>
        <div className="px-4 py-8">
          <div className="max-w-6xl mx-auto">
            <div className={`text-center py-12 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className={`text-6xl mb-4 ${
                darkMode ? 'text-red-400' : 'text-red-500'
              }`}>
                ⚠️
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Erreur de chargement
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Impossible de charger les données. Veuillez réessayer.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Réessayer
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen transition-colors duration-300" style={{
      background: darkMode 
        ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* App'Ines Banner */}
      <AppinesBanner className="md:pt-6" />
      
      <div className="pt-20 md:pt-0 px-4 pb-8">
        <div className="max-w-6xl mx-auto">
          <Breadcrumb items={breadcrumbItems} darkMode={darkMode} />
          
          {/* Header */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <button
                onClick={() => navigate(-1)}
                className={`mr-4 p-2 rounded-lg transition-colors ${
                  darkMode 
                    ? 'text-gray-300 hover:text-white hover:bg-gray-700' 
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
                aria-label="Retour"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <div className="flex items-center">
                <Hash className={`h-8 w-8 mr-2 ${
                  darkMode ? 'text-emerald-400' : 'text-emerald-600'
                }`} />
                <h1 className={`text-3xl md:text-4xl font-bold ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {originalHashtag}
                </h1>
              </div>
            </div>
            
            {!loading && (
              <p className={`text-lg ${
                darkMode ? 'text-gray-300' : 'text-gray-600'
              }`}>
                {filteredActs.length} acte{filteredActs.length > 1 ? 's' : ''} trouvé{filteredActs.length > 1 ? 's' : ''} 
                {filteredActs.length > 0 ? ' pour cette pathologie' : ' pour cette pathologie'}
              </p>
            )}
          </div>

          {/* Contenu principal */}
          {loading ? (
            <SkeletonGrid darkMode={darkMode} count={6} />
          ) : filteredActs.length === 0 ? (
            <div className={`text-center py-12 rounded-lg ${
              darkMode ? 'bg-gray-800' : 'bg-white'
            }`}>
              <div className={`text-6xl mb-4 ${
                darkMode ? 'text-gray-600' : 'text-gray-300'
              }`}>
                🔍
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Aucun acte trouvé
              </h3>
              <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
                Aucun acte de kinésithérapie n'est associé à cette pathologie.
              </p>
              <button
                onClick={() => navigate('/')}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                Retour à l'accueil
              </button>
            </div>
          ) : (
            <ResultsGrid
              results={filteredActs}
              cartItems={cartItems}
              onToggleCartItem={onToggleCartItem}
              darkMode={darkMode}
              loading={false}
              displayCotationType={displayCotationType}
              onHashtagClick={onHashtagClick}
            />
          )}
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </div>
  );
};

export default HashtagPage;